/* ==========================================================================
   Cherokee Hair Products — main.js
   Pass 1: announcement rotation, sticky nav scroll state, mobile menu,
   hero parallax, section reveal observer.
   ========================================================================== */

(() => {
  'use strict';

  /* ---------- Announcement bar: rotate messages + hide on scroll down ---- */
  const announcement = document.querySelector('[data-announcement]');
  if (announcement) {
    const messages = announcement.querySelectorAll('[data-announcement-message]');
    if (messages.length > 1) {
      let active = 0;
      messages[active].classList.add('is-active');
      setInterval(() => {
        messages[active].classList.remove('is-active');
        active = (active + 1) % messages.length;
        messages[active].classList.add('is-active');
      }, 4000);
    } else if (messages.length === 1) {
      messages[0].classList.add('is-active');
    }

    const closeBtn = announcement.querySelector('[data-announcement-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        announcement.classList.add('announcement--hidden');
        document.body.style.paddingTop =
          getComputedStyle(document.body).paddingTop; // hold height; will correct on resize
      });
    }
  }

  /* ---------- Sticky header: shrink on scroll; hide announcement on scroll down */
  const header = document.querySelector('[data-site-header]');
  let lastScroll = 0;
  const onScroll = () => {
    const y = window.scrollY;
    if (header) header.classList.toggle('is-scrolled', y > 100);

    if (announcement && !announcement.classList.contains('announcement--user-closed')) {
      const scrollingDown = y > lastScroll && y > 120;
      announcement.classList.toggle('announcement--hidden', scrollingDown);
    }
    lastScroll = y;
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  // Mark user-closed once the close button is pressed so scroll handler doesn't revive it
  const closeBtn = document.querySelector('[data-announcement-close]');
  if (closeBtn && announcement) {
    closeBtn.addEventListener('click', () => {
      announcement.classList.add('announcement--user-closed');
    });
  }

  /* ---------- Mobile menu toggle ---------- */
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const menuClose = document.querySelector('[data-mobile-menu-close]');

  // Saved focus — returned to on close, so keyboard users don't lose place.
  let lastFocusedBeforeMenu = null;

  const openMenu = () => {
    if (!mobileMenu) return;
    lastFocusedBeforeMenu = document.activeElement;
    mobileMenu.classList.add('is-open');
    mobileMenu.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Move focus to close button so keyboard users can dismiss.
    if (menuClose) menuClose.focus();
  };
  const closeMenu = () => {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('is-open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    // Restore focus to the element that opened the menu (usually the hamburger).
    if (lastFocusedBeforeMenu && typeof lastFocusedBeforeMenu.focus === 'function') {
      lastFocusedBeforeMenu.focus();
    }
  };

  if (menuToggle) menuToggle.addEventListener('click', openMenu);
  if (menuClose)  menuClose.addEventListener('click', closeMenu);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu();
  });

  const prefersReducedMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Autoplay loops: honor reduced motion ----------
     WCAG 2.2.2 — any autoplay content looping >5s needs a way to pause.
     prefers-reduced-motion is the category-wide mechanism: pause every
     autoplay loop, show poster frames only. */
  if (prefersReducedMotion) {
    document.querySelectorAll('video[autoplay][loop]').forEach((v) => {
      v.removeAttribute('autoplay');
      v.pause();
    });
  }

  /* ---------- Section reveal via IntersectionObserver ---------- */
  const revealEls = document.querySelectorAll('.reveal');
  if ('IntersectionObserver' in window && revealEls.length) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- Auto-advancing slider (shared) ----------
     Powers the ingredient slider AND the customer-feedback slider.
     Each slider root needs:
       - class or attribute marking it as a carousel
       - slide elements with a common selector
       - dot buttons with data-slide="<index>"
       - [data-slide-prev] / [data-slide-next] buttons (optional)
     Behavior:
       - Timer only runs when section is on-screen
       - Pauses on hover / focus-within / tab hidden
       - Reduced-motion short-circuits the timer
  */
  const initAutoSlider = (root, { slideSel, dotSel, intervalMs = 4000 }) => {
    if (!root || prefersReducedMotion) return;
    const slides = root.querySelectorAll(slideSel);
    const dots = root.querySelectorAll(dotSel);
    if (slides.length === 0) return;

    let activeIndex = 0;
    let timerId = null;
    let paused = false;
    let inView = false;

    const setActive = (index) => {
      slides[activeIndex]?.classList.remove('is-active');
      slides[index]?.classList.add('is-active');
      if (dots[activeIndex]) {
        dots[activeIndex].classList.remove('is-active');
        dots[activeIndex].setAttribute('aria-selected', 'false');
      }
      if (dots[index]) {
        dots[index].classList.add('is-active');
        dots[index].setAttribute('aria-selected', 'true');
      }
      activeIndex = index;
    };
    const goToSlide = (index) => {
      if (index === activeIndex) return;
      setActive(index);
    };
    const advance = () => {
      if (paused || !inView || document.hidden) return;
      goToSlide((activeIndex + 1) % slides.length);
    };
    const stopTimer = () => { if (timerId) { clearInterval(timerId); timerId = null; } };
    const startTimer = () => { stopTimer(); timerId = setInterval(advance, intervalMs); };
    const resetTimer = () => { if (inView && !document.hidden) startTimer(); };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const target = parseInt(dot.getAttribute('data-slide'), 10);
        if (!Number.isNaN(target)) {
          goToSlide(target);
          resetTimer();
        }
      });
    });

    const prevBtn = root.querySelector('[data-slide-prev]');
    const nextBtn = root.querySelector('[data-slide-next]');
    const arrowNav = (direction) => {
      stopTimer();
      setActive((activeIndex + direction + slides.length) % slides.length);
      setTimeout(() => { if (inView && !document.hidden) startTimer(); }, intervalMs);
    };
    if (prevBtn) prevBtn.addEventListener('click', () => arrowNav(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => arrowNav(1));

    root.addEventListener('mouseenter', () => { paused = true; });
    root.addEventListener('mouseleave', () => { paused = false; });
    root.addEventListener('focusin', () => { paused = true; });
    root.addEventListener('focusout', () => { paused = false; });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopTimer();
      else if (inView) startTimer();
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
          if (inView) startTimer();
          else stopTimer();
        });
      }, { threshold: 0.1 });
      io.observe(root);
    } else {
      inView = true;
      startTimer();
    }
  };

  initAutoSlider(document.querySelector('[data-ingredient-stage]'), {
    slideSel: '.ingredient-slide',
    dotSel: '.ingredient-nav__dot',
    intervalMs: 4000,
  });

  initAutoSlider(document.querySelector('[data-testimonial-stage]'), {
    slideSel: '.testimonial-slide',
    dotSel: '.testimonial-nav__dot',
    intervalMs: 6000,
  });
  /* ---------- Klaviyo form: parallel post on submit ---------------------- *
   * Runs only when the email-capture form was rendered with
   * data-klaviyo-form (set in email-capture.liquid when both theme
   * settings — Public API Key and Newsletter List ID — are filled in).
   *
   * Does NOT intercept or block the Shopify customer form submission;
   * the user still creates a Shopify customer record as before. We fire
   * the Klaviyo subscription in parallel so the email also lands on the
   * list without waiting on the Shopify→Klaviyo nightly sync.
   *
   * Uses Klaviyo's client-side subscriptions API which is designed for
   * the public key and handles double opt-in automatically per the list
   * settings in Klaviyo admin.
   * ---------------------------------------------------------------------- */
  /* ---------- Honeypot: block submission when bot fills hidden field ----- *
   * Any form containing input[name="contact[website]"] is honeypot-protected.
   * If a value is present, we silently block submission — the bot moves on
   * thinking it succeeded; nothing reaches Shopify or Klaviyo.
   * ---------------------------------------------------------------------- */
  document.querySelectorAll('form').forEach(form => {
    const hp = form.querySelector('input[name="contact[website]"]');
    if (!hp) return;
    form.addEventListener('submit', (e) => {
      if (hp.value && hp.value.trim() !== '') {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);  // capture phase so it runs before Klaviyo handler
  });

  document.querySelectorAll('[data-klaviyo-form]').forEach(marker => {
    const form = marker.closest('form');
    if (!form) return;
    const companyId = marker.dataset.klaviyoCompanyId;
    const listId = marker.dataset.klaviyoListId;
    if (!companyId || !listId) return;

    form.addEventListener('submit', () => {
      // Honeypot check (defensive — capture-phase blocker above should already prevent this)
      const hp = form.querySelector('input[name="contact[website]"]');
      if (hp && hp.value && hp.value.trim() !== '') return;

      const emailInput = form.querySelector('input[type="email"]');
      const email = emailInput ? emailInput.value.trim() : '';
      if (!email) return;

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email } } }
          },
          relationships: {
            list: { data: { type: 'list', id: listId } }
          }
        }
      };

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify(payload),
        keepalive: true  // survives page navigation after Shopify form submit
      }).catch(() => { /* Shopify form is the source of truth; Klaviyo is best-effort */ });
    });
  });

  /* ---------- Search drawer: predictive results via /search/suggest.json --- */
  (function initSearchDrawer() {
    const drawer = document.querySelector('[data-search-drawer]');
    if (!drawer) return;
    const input = drawer.querySelector('[data-search-input]');
    const resultsEl = drawer.querySelector('[data-search-results]');
    const toggles = document.querySelectorAll('[data-search-toggle]');
    const closes = drawer.querySelectorAll('[data-search-close]');

    const openDrawer = () => {
      drawer.setAttribute('aria-hidden', 'false');
      toggles.forEach(t => t.setAttribute('aria-expanded', 'true'));
      document.body.style.overflow = 'hidden';
      setTimeout(() => input.focus(), 50);
    };
    const closeDrawer = () => {
      drawer.setAttribute('aria-hidden', 'true');
      toggles.forEach(t => t.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = '';
    };

    toggles.forEach(t => t.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
    }));
    closes.forEach(c => c.addEventListener('click', closeDrawer));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
    });

    /* Safe DOM construction (no innerHTML with untrusted values) */
    const el = (tag, props = {}, children = []) => {
      const e = document.createElement(tag);
      Object.entries(props).forEach(([k, v]) => {
        if (k === 'class') e.className = v;
        else if (k === 'text') e.textContent = v;
        else e.setAttribute(k, v);
      });
      children.forEach(c => c && e.appendChild(c));
      return e;
    };
    /* Shopify suggest.json returns price as decimal string in dollars, e.g. "38.99". */
    const formatMoney = (value) => {
      const n = parseFloat(value);
      if (isNaN(n)) return '';
      return '$' + n.toFixed(2);
    };

    const renderProduct = (p) => {
      const img = p.image
        ? el('img', { class: 'search-drawer__result-img', src: p.image, alt: '', loading: 'lazy' })
        : el('div', { class: 'search-drawer__result-img' });
      const title = el('p', { class: 'search-drawer__result-title', text: p.title || '' });
      const body = el('div', { class: 'search-drawer__result-body' }, [title]);
      // Show a price range if min/max differ, otherwise single price
      const minStr = p.price_min || p.price || '';
      const maxStr = p.price_max || '';
      let priceText = '';
      if (minStr && maxStr && minStr !== maxStr) priceText = formatMoney(minStr) + ' – ' + formatMoney(maxStr);
      else if (minStr) priceText = formatMoney(minStr);
      if (priceText) body.appendChild(el('p', { class: 'search-drawer__result-meta', text: priceText }));
      return el('a', { class: 'search-drawer__result', href: p.url || '#' }, [img, body]);
    };
    const renderLink = (item) => el('a', { class: 'search-drawer__result', href: item.url || '#' }, [
      el('div', { class: 'search-drawer__result-body' }, [el('p', { class: 'search-drawer__result-title', text: item.title || '' })])
    ]);

    const renderResults = (data, query) => {
      resultsEl.textContent = '';
      const products = data.resources?.results?.products || [];
      const pages = data.resources?.results?.pages || [];
      const articles = data.resources?.results?.articles || [];
      const total = products.length + pages.length + articles.length;

      if (total === 0) {
        const empty = el('div', { class: 'search-drawer__empty' });
        empty.textContent = `No results for "${query}"`;
        resultsEl.appendChild(empty);
        return;
      }
      const addSection = (heading, items, renderFn) => {
        if (!items.length) return;
        const section = el('div', { class: 'search-drawer__results-section' }, [
          el('h3', { class: 'search-drawer__results-heading', text: heading })
        ]);
        items.forEach(i => section.appendChild(renderFn(i)));
        resultsEl.appendChild(section);
      };
      addSection('Products', products, renderProduct);
      addSection('Pages', pages, renderLink);
      addSection('Journal', articles, renderLink);

      const viewAll = el('a', { class: 'search-drawer__view-all', href: '/search?q=' + encodeURIComponent(query), text: 'View all results →' });
      resultsEl.appendChild(viewAll);
    };

    let debounceTimer;
    let currentRequest;
    const doSearch = (query) => {
      if (currentRequest) currentRequest.abort?.();
      const controller = new AbortController();
      currentRequest = controller;
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,article&resources[limit]=6&resources[options][unavailable_products]=last`;
      fetch(url, { signal: controller.signal })
        .then(r => r.json())
        .then(data => renderResults(data, query))
        .catch(err => {
          if (err.name === 'AbortError') return;
          resultsEl.textContent = '';
          const errMsg = el('div', { class: 'search-drawer__empty', text: 'Search unavailable. Try again.' });
          resultsEl.appendChild(errMsg);
        });
    };

    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(debounceTimer);
      if (q.length < 2) { resultsEl.textContent = ''; return; }
      resultsEl.textContent = '';
      const loading = el('div', { class: 'search-drawer__loading', text: 'Searching…' });
      resultsEl.appendChild(loading);
      debounceTimer = setTimeout(() => doSearch(q), 200);
    });
  })();

  /* ---------- Marketing popup: welcome discount + Klaviyo subscribe -------- */
  (function initMarketingPopup() {
    const popup = document.querySelector('[data-marketing-popup]');
    if (!popup) return;

    // Skip on cart, checkout, account paths
    const path = window.location.pathname;
    if (/^\/(cart|checkout|account)/.test(path)) return;

    const STORAGE_KEY = 'cherokee_popup_dismissed_at';
    const cooldownDays = parseInt(popup.dataset.popupCooldownDays, 10) || 14;
    const delaySec = parseInt(popup.dataset.popupDelay, 10) || 20;
    const trigger = popup.dataset.popupTrigger || 'delay';
    const companyId = popup.dataset.klaviyoCompanyId;
    const emailListId = popup.dataset.klaviyoEmailList;
    const smsListId = popup.dataset.klaviyoSmsList;

    // Cooldown check: don't show if dismissed within cooldown window
    const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEY), 10) || 0;
    const cooldownMs = cooldownDays * 24 * 60 * 60 * 1000;
    if (dismissedAt && Date.now() - dismissedAt < cooldownMs) return;

    const formEl = popup.querySelector('[data-popup-form]');
    const emailInput = popup.querySelector('[data-popup-email]');
    const phoneInput = popup.querySelector('[data-popup-phone]');
    const errorEl = popup.querySelector('[data-popup-error]');
    const formState = popup.querySelector('[data-popup-state="form"]');
    const successState = popup.querySelector('[data-popup-state="success"]');
    const closeButtons = popup.querySelectorAll('[data-popup-close]');

    let opened = false;

    const openPopup = () => {
      if (opened) return;
      opened = true;
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => emailInput.focus(), 100);
    };

    const closePopup = () => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    };

    closeButtons.forEach(btn => btn.addEventListener('click', closePopup));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) closePopup();
    });

    // Trigger
    if (trigger === 'exit-intent') {
      // Desktop: mouseout to top of viewport
      // Mobile: fallback to delay
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        setTimeout(openPopup, delaySec * 1000);
      } else {
        const onMouseOut = (e) => {
          if (e.clientY <= 0 && !opened) {
            openPopup();
            document.removeEventListener('mouseout', onMouseOut);
          }
        };
        document.addEventListener('mouseout', onMouseOut);
        // Fallback: also trigger after delay even on desktop
        setTimeout(openPopup, delaySec * 2 * 1000);
      }
    } else {
      setTimeout(openPopup, delaySec * 1000);
    }

    /* Subscribe to Klaviyo via client API */
    const subscribeToKlaviyo = (listId, profile) => {
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: profile } }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      };
      return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify(payload),
        keepalive: true
      });
    };

    /* Live US phone formatting: as user types, show "(555) 123-4567". */
    phoneInput.addEventListener('input', () => {
      let digits = phoneInput.value.replace(/\D/g, '');
      // Drop leading 1 (US country code)
      if (digits.length > 10 && digits.startsWith('1')) digits = digits.slice(1);
      digits = digits.slice(0, 10);
      let out = '';
      if (digits.length === 0) out = '';
      else if (digits.length < 4) out = digits;
      else if (digits.length < 7) out = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      else out = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      phoneInput.value = out;
    });

    formEl.addEventListener('submit', (e) => {
      e.preventDefault();
      errorEl.hidden = true;

      // Honeypot
      const hp = formEl.querySelector('input[name="contact[website]"]');
      if (hp && hp.value.trim()) { closePopup(); return; }

      // Email: trim + lowercase, validate
      const email = (emailInput.value || '').trim().toLowerCase();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errorEl.textContent = 'Please enter a valid email address.';
        errorEl.hidden = false;
        emailInput.focus();
        return;
      }

      // Phone: optional, but if provided must be 10 digits (US)
      const phoneDigits = (phoneInput.value || '').replace(/\D/g, '');
      let phoneE164 = '';
      if (phoneDigits) {
        const tenDigit = phoneDigits.length === 11 && phoneDigits.startsWith('1')
          ? phoneDigits.slice(1)
          : phoneDigits;
        if (tenDigit.length !== 10) {
          errorEl.textContent = 'Please enter a 10-digit US phone number, or leave blank.';
          errorEl.hidden = false;
          phoneInput.focus();
          return;
        }
        phoneE164 = '+1' + tenDigit;
      }

      // Fire requests (best effort; success UI shows regardless of network result)
      const requests = [];
      requests.push(subscribeToKlaviyo(emailListId, { email }));
      if (phoneE164) {
        requests.push(subscribeToKlaviyo(smsListId, { email, phone_number: phoneE164 }));
      }
      Promise.allSettled(requests).catch(() => {});

      // Swap to success state
      formState.hidden = true;
      successState.hidden = false;
      // Mark dismissed so it doesn't reappear next visit
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    });
  })();

  /* ---------- Footer accordion: close on mobile, open on desktop ---------- */
  const footerAccordions = document.querySelectorAll('.site-footer__accordion');
  const closeOnMobile = () => {
    const isMobile = window.innerWidth < 960;
    footerAccordions.forEach(d => {
      if (isMobile) d.removeAttribute('open');
      else d.setAttribute('open', '');
    });
  };
  closeOnMobile();
  window.addEventListener('resize', closeOnMobile);
})();
